import { ActiveSubscriptionStatuses, PlatformUser, PlatformUserClient } from 'models/user'
import { EntityKeys, QueryType } from 'utils/reactQuery'

import { QueryClient } from '@tanstack/react-query'
import { logger } from 'utils/logging'

const USER_ME_QUERY_KEY = [EntityKeys.USER_ME, QueryType.GET]

const DEFAULT_MAX_ATTEMPTS = 4
const DEFAULT_DELAY_MS = 1500

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The workspace's active-membership entry from the cached userMe, or undefined (same conditions as
 * UserDataContext's getWorkspaceItemById). Exported for callers that need the entry itself — reading
 * the cache directly sidesteps the stale-closure trap: a callback captured before the membership
 * landed still holds the pre-refetch workspace arrays, while the cache is always current.
 */
export const getWorkspaceMembershipFromCache = (queryClient: QueryClient, workspaceId: string) => {
  const userMe = queryClient.getQueryData<PlatformUser | null>(USER_ME_QUERY_KEY)
  const workspaces = (userMe as PlatformUserClient | null | undefined)?.workspaces

  return workspaces?.find((workspace) => workspace.id === workspaceId && workspace.isMember && ActiveSubscriptionStatuses.has(workspace.status))
}

/** Whether the cached userMe already resolves the workspace as an active membership. */
const isWorkspaceMemberInCache = (queryClient: QueryClient, workspaceId: string): boolean =>
  !!getWorkspaceMembershipFromCache(queryClient, workspaceId)

/**
 * Bounded userMe polling until a workspace resolves as an active membership.
 *
 * Two flows share the same propagation race: accepting an invitation (QA-258) and creating a
 * workspace (ISSUE-2896). Both write the chosen workspace id before userMe can resolve it, and
 * nothing refetches userMe on its own — the query has no refetchInterval, refetchOnWindowFocus is
 * disabled globally and every USER_ME invalidation is bound to an explicit user action. Without a
 * follow-up refetch the switch stalls until a manual reload.
 *
 * Refetches userMe until the workspace resolves as an active membership, capped at `maxAttempts`.
 * A membership already visible in the cache resolves immediately, with no delay and no refetch —
 * so a caller that has just awaited its own refetch (workspace creation) pays nothing when that
 * refetch already carried the membership. Resolution itself stays in UserDataContext — once a
 * refetch returns the propagated data its resolution effect re-runs and completes the switch; this
 * helper never writes any state. May be called fire-and-forget (invitation accept) or awaited as a
 * navigation gate (workspace creation).
 *
 * @returns true once the membership is visible in the cache, false when attempts are exhausted
 */
export const pollUserMeUntilWorkspaceMember = async (
  queryClient: QueryClient,
  workspaceId: string,
  { maxAttempts = DEFAULT_MAX_ATTEMPTS, delayMs = DEFAULT_DELAY_MS }: { maxAttempts?: number, delayMs?: number } = {}
): Promise<boolean> => {
  if (isWorkspaceMemberInCache(queryClient, workspaceId)) return true

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await delay(delayMs)

    // The membership may have landed in the meantime (e.g. via the accept handler's own invalidation).
    if (isWorkspaceMemberInCache(queryClient, workspaceId)) return true

    await queryClient.refetchQueries({ queryKey: USER_ME_QUERY_KEY, exact: true })

    if (isWorkspaceMemberInCache(queryClient, workspaceId)) return true
  }

  // The observed query state distinguishes the root causes this one line otherwise conflates:
  // backend propagation lag (success + fresh dataUpdatedAt, membership just absent), a broken
  // userMe endpoint (error + errorUpdateCount), and an offline tab (fetchStatus 'paused' — note
  // refetchQueries resolves instantly for a paused query, so nothing else records that case).
  const queryState = queryClient.getQueryState(USER_ME_QUERY_KEY)
  logger.warn('Workspace membership did not propagate to userMe within the polling budget', undefined, {
    context: {
      component: 'pollUserMeUntilWorkspaceMember',
      action: 'poll',
      metadata: {
        workspaceId,
        maxAttempts,
        delayMs,
        queryStatus: queryState?.status,
        fetchStatus: queryState?.fetchStatus,
        dataUpdatedAt: queryState?.dataUpdatedAt,
        errorUpdateCount: queryState?.errorUpdateCount,
      },
    }
  })

  return false
}
